import { FormControl, FormHelperText, InputLabel, OutlinedInput } from "@mui/material";
import { forwardRef } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { IMaskInput } from "react-imask";

/**
 * FormMaskedInputBase
 */

interface CustomProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
  mask: string;
}

const FormMaskedInputBase = forwardRef<HTMLInputElement, CustomProps>((props, refs) => {
  const { onChange, ...other } = props;
  return (
    <IMaskInput
      {...other}
      placeholderChar="_"
      inputRef={refs}
      onAccept={(value: any, input: { unmaskedValue: string }) => {
        if (input.unmaskedValue.length > 0) {
          onChange({ target: { name: props.name, value } });
        } else {
          onChange({ target: { name: props.name, value: '' } });
        }
      }}
      lazy={false}
      overwrite={true}
      unmask={false}
    />
  );
});

/**
 * FormNotaFiscalInput
 */

type Props<T extends {}> = {
  name: keyof T,
  label: string,
  required?: boolean,
  readOnly?: boolean,
  reactHookForm: UseFormReturn<T>,
};

export const FormNotaFiscalInput = <T extends {},>({
  name,
  label,
  required,
  readOnly,
  reactHookForm,
}: Props<T>) => {
  return (
    <Controller
      name={name as any}
      control={reactHookForm.control}
      render={({ field, formState }) => {
        let helperText: string | null = null;
        let value = '';

        if (typeof field.value === 'string') {
          value = field.value.replace('/', '');
        } else {
          value = '';
        }

        if (name in formState.errors) {
          const error = formState.errors[name] as any;
          if (typeof error.message === 'string') {
            helperText = error.message;
          }
        }

        return (
          <FormControl variant="outlined" size="small">
            <InputLabel
              htmlFor={`input-${name as string}`}
              error={helperText !== null}
              shrink={true}
              size="small"
              required={required}>
              {label}
            </InputLabel>

            <OutlinedInput
              {...field}
              size="small"
              id={`input-${name as string}`}
              name={name as string}
              label={label}
              notched={true}
              readOnly={readOnly}
              required={required}
              disabled={formState.disabled}
              aria-describedby={`${name as string}-component-error-text`}
              inputComponent={FormMaskedInputBase as any}
              value={value}
              inputProps={{
                mask: [
                  { mask: '0' },
                  { mask: '00' },
                  { mask: '\\0/\\00' },
                  { mask: '\\0/00' },
                  { mask: '0/00' },
                  { mask: '00/00' },
                  { mask: '000/00' },
                  { mask: '0000/00' },
                  { mask: '00000/00' },
                  { mask: '000000/00' },
                  { mask: '0000000/00' },
                  { mask: '00000000/00' },
                  { mask: '000000000/00' },
                ],
                placeholder: "_________/__",
                required: required,
              }} />

            <FormHelperText id={`${name as string}-component-error-text`} error={true}>
              {helperText}
            </FormHelperText>
          </FormControl>
        );
      }} />
  );
}